import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { buttonClass, type ButtonSize, type ButtonVariant } from './button-class';
import { Icon, type IconName } from './icon';

/**
 * A legacy-styled button. `icon` goes before the label; `iconOnly` hides the label visually but
 * keeps it as the accessible name; `loading` swaps the icon for a spinner and disables the button.
 */
export function Button({
  variant = 'secondary',
  size = 'md',
  outline = false,
  block = false,
  icon,
  iconOnly = false,
  wrap = false,
  loading = false,
  type = 'button',
  disabled,
  className = '',
  children,
  ...rest
}: Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  outline?: boolean;
  block?: boolean;
  icon?: IconName;
  iconOnly?: boolean;
  /** Let a long label wrap instead of overflowing. */
  wrap?: boolean;
  loading?: boolean;
  children?: ReactNode;
}) {
  const shownIcon: IconName | undefined = loading ? 'spinner' : icon;
  return (
    <button
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={`${buttonClass({ variant, size, outline, block, iconOnly, wrap })} ${className}`}
      {...rest}
      title={rest.title ?? (iconOnly && typeof children === 'string' ? children : undefined)}
    >
      {shownIcon ? <Icon name={shownIcon} spin={loading} /> : null}
      {iconOnly ? <span className="sr-only">{children}</span> : children}
    </button>
  );
}
